import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { QuantityStepper } from '@/components/QuantityStepper/QuantityStepper';
import { Thumb } from '@/components/Thumb/Thumb';
import { useCart } from '@/context/CartContext';
import { useCartItems } from '@/hooks/useCartItems';
import { formatPrice } from '@/utils/format';
import { requiresDelivery } from '@/utils/pricing';
import { paths } from '@/routes/paths';
import styles from './CartDrawer.module.css';

export function CartDrawer() {
  const navigate = useNavigate();
  const cart = useCart();
  const { groups, unavailable, isCatalogReady, isMultiCurrency } = useCartItems();
  const closeRef = useRef(null);

  // Escape closes the drawer, and the page behind it must not scroll while open.
  useEffect(() => {
    if (!cart.isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') cart.close();
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [cart.isOpen, cart.close, cart]);

  if (!cart.isOpen) return null;

  const goToCheckout = (currency) => {
    cart.close();
    navigate(paths.checkout(currency));
  };

  return (
    <>
      <button
        type="button"
        className={styles.scrim}
        aria-label="Close cart"
        tabIndex={-1}
        onClick={cart.close}
      />

      <aside
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-drawer-title"
      >
        <div className={styles.head}>
          <h2 id="cart-drawer-title" className={styles.title}>
            Your cart ({cart.count})
          </h2>
          <button ref={closeRef} type="button" className={styles.close} onClick={cart.close}>
            <span aria-hidden="true">✕</span>
            <span className="sr-only">Close cart</span>
          </button>
        </div>

        {cart.isEmpty ? (
          <div className={styles.empty}>
            <div className={styles.emojiBig} aria-hidden="true">
              🛒
            </div>
            <div className={styles.emptyTitle}>Nothing in here yet</div>
            <div>Your future favorite thing is one click away.</div>
          </div>
        ) : !isCatalogReady ? (
          // The cart is not empty, the catalog just hasn't answered yet. Saying
          // "nothing in here" would be wrong, and briefly alarming.
          <div className={styles.empty}>Loading your cart…</div>
        ) : (
          <div className={styles.groups}>
            {/* Publishers price in their own currency, so a cart can hold both
                an INR and a USD book. Those amounts can't be added up, so the
                cart is split into one per currency and each is bought on its
                own — never a single mixed total. */}
            {isMultiCurrency && (
              <p className={styles.splitNote}>
                Your books are priced in {groups.map((g) => g.currency).join(' and ')}, so
                they&apos;re checked out separately — one currency at a time.
              </p>
            )}

            {/* Books that have left the catalog. Shown rather than dropped: the
                header counts them, so hiding them leaves a cart that can't be
                emptied. Nothing can be priced or bought here — only removed. */}
            {unavailable.length > 0 && (
              <section className={styles.group}>
                <h3 className={styles.groupTitle}>No longer available</h3>
                <ul className={styles.list}>
                  {unavailable.map((line) => (
                    <li key={line.key} className={`${styles.item} ${styles.itemGone}`}>
                      <div className={styles.itemBody}>
                        <div className={styles.itemName}>This book is no longer on sale</div>
                        <div className={styles.itemLangs}>
                          It was withdrawn after you added it, so it can&apos;t be bought.
                        </div>
                      </div>
                      <button
                        type="button"
                        className={styles.remove}
                        onClick={() => cart.remove(line.key)}
                        title="Remove"
                      >
                        <span aria-hidden="true">✕</span>
                        <span className="sr-only">Remove unavailable item</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {groups.map((group) => (
              <section key={group.currency} className={styles.group}>
                {isMultiCurrency && (
                  <h3 className={styles.groupTitle}>
                    Priced in {group.currency}
                    <span className={styles.groupCount}>
                      {group.items.length} {group.items.length === 1 ? 'book' : 'books'}
                    </span>
                  </h3>
                )}

                <ul className={styles.list}>
                  {group.items.map((item) => (
                    <li key={item.key} className={styles.item}>
                      <Thumb
                        letter={item.letter}
                        tint={item.tint}
                        deep={item.deep}
                        src={item.image_url}
                        size="sm"
                      />

                      <div className={styles.itemBody}>
                        <Link
                          to={paths.product(item.id)}
                          className={styles.itemName}
                          onClick={cart.close}
                        >
                          {item.name}
                        </Link>

                        {item.languages?.length > 0 && (
                          <div className={styles.itemLangs}>+ {item.languages.join(', ')}</div>
                        )}

                        <div className={styles.itemRow}>
                          {/* A digital book is bought once per account — a
                              stepper here would offer to charge twice for the
                              same unlock. */}
                          {requiresDelivery(item) ? (
                            <QuantityStepper
                              size="sm"
                              value={item.qty}
                              label={`quantity for ${item.name}`}
                              onIncrement={() => cart.changeQty(item.key, 1)}
                              onDecrement={() => cart.changeQty(item.key, -1)}
                            />
                          ) : (
                            <span className={styles.singleCopy}>Digital · 1 copy</span>
                          )}
                          <span className={styles.lineTotal}>
                            {formatPrice(item.lineTotal, item.currency)}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className={styles.remove}
                        onClick={() => cart.remove(item.key)}
                        title="Remove"
                      >
                        <span aria-hidden="true">✕</span>
                        <span className="sr-only">Remove {item.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>

                <div className={styles.groupFoot}>
                  <div className={styles.subtotalRow}>
                    <span className={styles.subtotalLabel}>Subtotal</span>
                    <span className={styles.subtotalValue}>
                      {formatPrice(group.totals.subtotal, group.currency)}
                    </span>
                  </div>

                  <Button block onClick={() => goToCheckout(group.currency)}>
                    {isMultiCurrency ? `Checkout ${group.currency} →` : 'Checkout →'}
                  </Button>
                </div>
              </section>
            ))}
          </div>
        )}
      </aside>
    </>
  );
}
